import React from 'react';
import SaveToListModal from '../../../../components/lists/SaveToListModal';
import { onRoadblockedClick } from '../../../../utils/roadblocks';
import { PageStrings } from '../../../../utils/page_strings_util';
import { ServerCommunicationUtil } from '../../../../utils/server_communication_util';
import { StringConstantsMixin } from './shared/string_constants_mixin';
import { ModalFunnelUtil } from '../../../../utils/modal_funnel_util';

// Used by SearchApp (SearchMap/SearchResultList) and by FullScreenSearchResultList
// Depends on functionality in ListItemRequestMixin and ListHashMixin
const ResultCardFunctionsMixin = {
  humanizePageName(str) {
    // https://stackoverflow.com/a/21792507 - because didn't feel like using brain to code this
    const frags = str.split('_');
    for (let i = 0; i < frags.length; i += 1) {
      frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1).toLowerCase();
    }
    return frags.join(' ');
  },
  humanizeType(type) {
    return type.charAt(0).toUpperCase() + type.slice(1);
  },
  humanizeListId(listId) {
    switch (listId) {
      case this.favoriteMagicId:
        return 'Favorite';
      case this.completedMagicId:
        return 'Completed';
      case this.verifiedMagicId:
        return 'Verified';
      default:
        return 'List Item';
    }
  },
  notifyGA(my, listId, type, objectId) {
    let { page } = this.state;
    if (!page || Number.isInteger(page)) {
      page = this.props.initialPage;
    }
    if (!page) {
      page = PageStrings.EXPLORE_ALL_PAGE;
    }
    if (this.isItemInList(my, listId, type, objectId)) {
      ServerCommunicationUtil.handleGaqPush([
        '_trackEvent',
        this.humanizePageName(page),
        `${this.humanizeListId(listId)} Removed`,
        `${this.humanizeType(type)}`
      ]);
    } else {
      ServerCommunicationUtil.handleGaqPush([
        '_trackEvent',
        this.humanizePageName(page),
        `${this.humanizeListId(listId)} Added`,
        `${this.humanizeType(type)}`
      ]);
    }
  },
  allowAddRemove() {
    // Only allow on completed page, when admin or current user
    return (
      [
        PageStrings.EXPLORE_COMPLETED_PAGE,
        PageStrings.EXPLORE_FAVORITE_PAGE,
        PageStrings.EXPLORE_CUSTOM_PAGE,
        PageStrings.EXPLORE_USERS_TRACKS_PAGE,
        PageStrings.EXPLORE_USERS_MAPS_PAGE
      ].includes(this.state.page) &&
      this.props.context.currentUser &&
      this.props.userInfo &&
      (this.props.context.currentUser.admin || this.props.context.currentUser.id == this.props.userInfo.id)
    );
  },
  allowClickingCompletedBadge() {
    // Only allow on completed page, when admin
    return (
      this.state.page === PageStrings.EXPLORE_COMPLETED_PAGE &&
      this.props.context &&
      this.props.context.currentUser &&
      this.props.context.currentUser.admin
    );
  },
  forcePhotoId(type, objectId) {
    if (['map', 'track'].indexOf(type) === -1) {
      return null;
    }
    // Currently only works for map/track photos
    return this.state.forceTrackPhotoIds ? this.state.forceTrackPhotoIds[objectId] : this.props.initialPhotoId;
  },
  getAddRemoveLabels() {
    switch (this.state.page) {
      case PageStrings.EXPLORE_COMPLETED_PAGE:
        return {
          add: StringConstantsMixin.strings.SEARCH.ADD_TO_COMPLETED,
          remove: StringConstantsMixin.strings.SEARCH.REMOVE_FROM_COMPLETED
        };
      case PageStrings.EXPLORE_FAVORITE_PAGE:
        return {
          add: StringConstantsMixin.strings.SEARCH.ADD_TO_FAVORITES,
          remove: StringConstantsMixin.strings.SEARCH.REMOVE_FROM_FAVORITES
        };
      case PageStrings.EXPLORE_CUSTOM_PAGE:
        return {
          add: StringConstantsMixin.strings.SEARCH.ADD_TO_LIST,
          remove: StringConstantsMixin.strings.SEARCH.REMOVE_FROM_LIST
        };
      case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      case PageStrings.EXPLORE_USERS_MAPS_PAGE:
        return {
          add: null,
          remove: StringConstantsMixin.strings.DELETE
        };
      default:
        return {};
    }
  },
  handleAddRemoveClick(type, objectId) {
    switch (this.state.page) {
      case PageStrings.EXPLORE_COMPLETED_PAGE:
        return this.handleAddToCompletedClick(type, objectId);
      case PageStrings.EXPLORE_FAVORITE_PAGE:
        return this.handleAddToFavoritesClick(type, objectId);
      case PageStrings.EXPLORE_CUSTOM_PAGE:
        return this.handleAddToListClick(type, objectId);
      case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      case PageStrings.EXPLORE_USERS_MAPS_PAGE:
        return this.handleDeleteMapClick(type, objectId);
      default:
        return null;
    }
  },
  handleDeleteMapClick(type, objectId) {
    if (!confirm(StringConstantsMixin.strings.DELETE_ITEM_QUESTION)) {
      return;
    }
    const url = `/api/alltrails/maps/${objectId}`;
    ServerCommunicationUtil.deleteApiEndpoint(
      url,
      {},
      a => {
        const results = this.state.results.filter(result => result.ID != objectId);
        this.setState({ results });
      },
      () => {},
      () => {}
    );
  },
  handleAddToCompletedClick(type, objectId) {
    if (!this.props.context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      ModalFunnelUtil.modalSignup(null, `${type}-resultcard-addtocompleted`, returnToUrl);
    } else {
      // Only allow signed-in users to do this
      let userId;
      // Only on the completed page, does a completed badge not represent the current user's completed (it represents
      // the completed status of the list's user).
      // Only allow admins to update another user's completed list
      if (
        this.state.page === PageStrings.EXPLORE_COMPLETED_PAGE &&
        this.props.context.currentUser &&
        this.props.context.currentUser.admin &&
        this.props.userInfo
      ) {
        userId = this.props.userInfo.id;
      } else {
        userId = null;
      }
      const listId = this.completedMagicId;
      this.notifyGA(!userId, listId, type, objectId);
      this.toggleInList(userId, listId, type, objectId);
    }
  },
  handleAddToListClick(type, objectId) {
    if (!this.props.context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      ModalFunnelUtil.modalSignup(null, `${type}-resultcard-addtolist`, returnToUrl);
    } else if (this.props.context.currentUser && this.state.listId) {
      // Only allow signed-in users to do this
      const userId = null;
      const { listId } = this.state;
      this.notifyGA(!userId, listId, type, objectId);
      this.toggleInList(userId, listId, type, objectId);
    }
  },
  handleAddToFavoritesClick(type, objectId) {
    if (!this.props.context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      ModalFunnelUtil.modalSignup(null, `${type}-resultcard-addtolist`, returnToUrl);
    } else if (this.props.context.currentUser && this.state.listId) {
      // Only allow signed-in users to do this
      const userId = null;
      const listId = this.favoriteMagicId;
      this.notifyGA(!userId, listId, type, objectId);
      this.toggleInList(userId, listId, type, objectId);
    }
  },
  handleCompletedBadgeClick(type, objectId) {
    if (this.props.context.currentUser.admin) {
      // Only allow admins to manually verify ANY user's trails
      let userId;
      if (
        this.state.page === PageStrings.EXPLORE_COMPLETED_PAGE &&
        this.props.context.currentUser &&
        this.props.context.currentUser.admin &&
        this.props.userInfo &&
        this.props.context.currentUser.id != this.props.userInfo.id
      ) {
        userId = this.props.userInfo.id;
      } else {
        userId = null;
      }
      const listId = this.verifiedMagicId;
      this.notifyGA(!userId, listId, type, objectId);
      this.toggleInList(userId, listId, type, objectId);
    }
  },
  handleFavoriteClick(type, objectId) {
    if (!this.props.context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      if (this.props.mobileBrowser && this.messagingChannel) {
        onRoadblockedClick(this.messagingChannel, 'trail_nearby_save', this.props.context.currentUser, returnToUrl);
      } else {
        ModalFunnelUtil.modalSignup(null, `${type}-resultcard-favorite`, returnToUrl);
      }
    } else {
      this.openSaveToListModal(type, objectId);
    }
  },
  handleModalFavoriteClick(listId, type, objectId) {
    const userId = null;
    this.notifyGA(!userId, listId, type, objectId);
    this.toggleInList(userId, listId, type, objectId);
  },
  isItemInPageList(type, objectId) {
    switch (this.state.page) {
      case PageStrings.EXPLORE_FAVORITE_PAGE:
        return this.isItemFavorited(type, objectId);
      case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      case PageStrings.EXPLORE_USERS_MAPS_PAGE:
        return true;
      default:
        return this.isItemInList(true, this.state.listId, type, objectId);
    }
  },
  isItemCompleted(type, objectId) {
    const my = this.state.page !== PageStrings.EXPLORE_COMPLETED_PAGE;
    return this.isItemInList(my, this.completedMagicId, type, objectId);
  },
  // Function defines whether or not an item is in ANY of a user's lists, not just the favorites list
  isItemFavorited(type, objectId) {
    const my = true;
    const listIds = this.getListIdsInHash(my);
    return listIds.some(listId => {
      if (listId == this.completedMagicId || listId == this.verifiedMagicId) {
        return false;
      }
      return this.isItemInList(my, listId, type, objectId);
    });
  },
  isItemVerified(type, objectId) {
    const my = this.state.page !== PageStrings.EXPLORE_COMPLETED_PAGE;
    return this.isItemInList(my, this.verifiedMagicId, type, objectId);
  },
  showFavoriteHeart() {
    // Don't show on explore completed pages to simplify UI
    if (this.state.page === PageStrings.EXPLORE_COMPLETED_PAGE) {
      return false;
    }
    // Otherwise, always show favorite on trail card if possible (may be overriden by other action button)
    return true;
  },
  resetSaveToList() {
    this.setState({
      saveToList: null
    });
    // makes background scrollable again
    $('html').css('overflow', 'unset');
  },
  openSaveToListModal(type, objectId) {
    this.setState({
      saveToList: {
        type,
        objectId
      }
    });
    // makes background unscrollable
    $('html').css({ overflow: 'hidden' });
  },
  renderSaveToListModal() {
    if (!this.state.saveToList) {
      return null;
    }

    return (
      <SaveToListModal
        handleClose={this.resetSaveToList}
        handleFavoriteClick={this.handleModalFavoriteClick}
        listHashOwner={this}
        saveToList={this.state.saveToList}
      />
    );
  },
  // Catch-All for functions in this mixin
  // Keep this function at the bottom of this mixin
  getResultCardFunctions() {
    const self = this; // prevents weird scoping of `this` when inside object in ES6
    return {
      // Displaying photos
      forcePhotoId: self.forcePhotoId,
      // Displaying buttons
      allowClickingCompletedBadge: self.allowClickingCompletedBadge,
      allowAddRemove: self.allowAddRemove,
      showFavoriteHeart: self.showFavoriteHeart,
      // Filling button states
      getAddRemoveLabels: self.getAddRemoveLabels,
      isItemInPageList: self.isItemInPageList,
      isItemCompleted: self.isItemCompleted,
      isItemFavorited: self.isItemFavorited,
      isItemVerified: self.isItemVerified,
      // Handle clicking on entire result card (to best passed as handleCardClick)
      // These should be defined outside of the ResultCardFunctionsMixin
      handleAddRemoveClick: self.handleAddRemoveClick,
      handleMapClick: self.handleMapClick,
      handleTrackClick: self.handleTrackClick,
      handleTrailClick: self.handleTrailClick,
      formatTrailUrl: self.formatTrailUrl,
      // Handle clicking on buttons on result card
      handleCompletedBadgeClick: self.handleCompletedBadgeClick,
      handleFavoriteClick: self.handleFavoriteClick,
      handleLocationClick: self.handleTrailLocationClick
    };
  }
};

export { ResultCardFunctionsMixin };
